import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_TOKEN } from 'constants/constants';
import {
  ICustomerObject,
  IRegistrationDbCustomer,
} from 'pages/Shared/DealerCustomers/Models';

export interface IActiveImport {
  status: 'running' | 'idle';
  dataImportLog?: {
    id: number;
    fileName: string;
    startTime: string;
    endTime: string;
    entityType?: string;
    status: string;
    importSummary?: {
      totalRows: number;
      successfulRows: number;
      failedRows: number;
      duplicateRows: number;
      error?: string;
    };
  };
}

const token = localStorage.getItem(API_TOKEN);

export const dealerCustomerApi = createApi({
  reducerPath: 'dealerCustomerApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_ACTIVITY_BASE_URL,
    prepareHeaders: (headers) => {
      const authToken = localStorage.getItem(API_TOKEN);
      if (authToken) {
        headers.set('Authorization', `Bearer ${authToken}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Customers', 'OneCustomer', 'RegistrationDbCustomer'],
  keepUnusedDataFor: 60, // Keep unused data in cache for 60 seconds
  endpoints: (builder) => ({
    getCustomerList: builder.query<ICustomerObject[], string>({
      query: (dealerId) => `/v1/dealer-customers?dealerId=${dealerId}`,
      providesTags: ['Customers'],
      // Add cache configuration
      keepUnusedDataFor: 60, // Keep unused data in cache for 60 seconds
    }),
    getOnlyOwnCustomerList: builder.query<ICustomerObject[], string>({
      query: (dealerId) =>
        `/v1/dealer-customers?dealerId=${dealerId}&showSubDealerCustomers=false`,
      providesTags: ['Customers'],
    }),
    getOneCustomer: builder.query<ICustomerObject, number | string>({
      query: (id) => `/v1/dealer-customers/${id}`,
      providesTags: ['OneCustomer'],
    }),
    getRegistrationDbCustomerInfo: builder.query<
      IRegistrationDbCustomer,
      string
    >({
      query: (ticketId) => `v1/registration/db/customers/${ticketId}`,
      providesTags: ['RegistrationDbCustomer'],
    }),
    addNewCustomer: builder.mutation<[], ICustomerObject>({
      query: (data) => {
        const body = {
          dealerId: data.dealerInfo.dealerId,
          customerInfo: data.customerInfo,
        };
        return {
          url: '/v1/dealer-customers',
          method: 'Post',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body,
        };
      },
      invalidatesTags: ['Customers', 'OneCustomer'],
    }),
    updateCustomer: builder.mutation<[], ICustomerObject>({
      query: (data) => {
        return {
          url: `/v1/dealer-customers/${data.dealerCustomerId}`,
          method: 'Put',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: data,
        };
      },
      invalidatesTags: ['Customers', 'OneCustomer'],
    }),
    checkCustomerExists: builder.query<{ customerExists: boolean }, string>({
      query: (customerId) => `/v1/customers/is-unique/${customerId}`,
      // Add cache configuration
      keepUnusedDataFor: 60, // Keep unused data in cache for 60 seconds
    }),
    importCustomer: builder.mutation<
      IActiveImport,
      { file: File; entityType: string }
    >({
      query: ({ file, entityType }) => {
        const formData = new FormData();
        formData.append('files.file', file);
        formData.append('data', JSON.stringify({ entityType }));
        return {
          url: '/customers/import',
          method: 'POST',
          body: formData,
        };
      },
    }),
    checkImportStatus: builder.query<IActiveImport, string>({
      query: (entity) => `/data-import-logs/active/import?entityType=${entity}`,
      // Add cache configuration
      keepUnusedDataFor: 30, // Keep unused data in cache for 30 seconds
    }),
  }),
});

export const {
  useGetCustomerListQuery,
  useGetOnlyOwnCustomerListQuery,
  useGetOneCustomerQuery,
  useGetRegistrationDbCustomerInfoQuery,
  useAddNewCustomerMutation,
  useUpdateCustomerMutation,
  useImportCustomerMutation,
  useCheckImportStatusQuery,
} = dealerCustomerApi;
